import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "risa-oasis-ui_v2";
import { RootState } from "../../../redux/store/store";
import type {
  SupportTicketDetails,
  SupportTicketStatus,
} from "../supportTicketTypes";
import { isRisaTeamEmail } from "../utils/isRisaTeamEmail";
import {
  SUPPORT_TICKET_EDITABLE_STATUSES,
  SUPPORT_TICKET_STATUS_LABELS,
  SUPPORT_TICKET_STATUS_TAG_COLORS,
} from "../utils/supportTicketStatusDisplay";
import SupportTicketDetailField from "./supportTicketDetailField";

/**
 * Props for {@link SupportTicketDetailsPanel}.
 */
interface SupportTicketDetailsPanelProps {
  details: SupportTicketDetails;
  /** When true, the Set as Archive action is hidden (already in archive view). Shown to all users when false. */
  isArchiveActive?: boolean;
  /** Persists the edited status when Save is clicked. */
  onUpdateStatus?: (status: SupportTicketStatus) => Promise<boolean>;
  /** Archives the ticket via `is_archived: true`. */
  onArchiveTicket?: () => Promise<boolean>;
  /** Whether the status-save request is in flight. */
  isUpdatingStatus?: boolean;
  /** Whether the archive request is in flight. */
  isArchiving?: boolean;
}

/**
 * Right-side metadata panel for the selected support ticket.
 *
 * Status editing remains RISA-team only (`@risalabs.ai`). Archiving is available
 * to every authenticated user while the active-tickets view is showing.
 *
 * @param props - Ticket metadata values and optional status-save handlers.
 * @returns Bordered details panel with patient and workflow fields.
 */
const SupportTicketDetailsPanel = ({
  details,
  isArchiveActive = false,
  onUpdateStatus,
  onArchiveTicket,
  isUpdatingStatus = false,
  isArchiving = false,
}: SupportTicketDetailsPanelProps) => {
  const userEmail = useSelector(
    (state: RootState) => state.firebaseAuthentication.user?.email,
  );
  const canEditStatus = isRisaTeamEmail(userEmail);
  const [status, setStatus] = useState<SupportTicketStatus>(details.status);
  const statusColors = SUPPORT_TICKET_STATUS_TAG_COLORS[status];
  const hasStatusChanged = canEditStatus && status !== details.status;
  const isSaveDisabled = !hasStatusChanged || isUpdatingStatus;
  const showArchiveButton = !isArchiveActive;
  const showActionFooter = canEditStatus || showArchiveButton;

  useEffect(() => {
    setStatus(details.status);
  }, [details.status]);

  /**
   * Persists the currently selected status when Save is clicked.
   *
   * @returns Resolves when the status update finishes or is skipped.
   */
  const handleSaveStatus = async () => {
    if (isSaveDisabled) {
      return;
    }

    await onUpdateStatus?.(status);
  };

  /**
   * Archives the ticket via POST /update with `is_archived: true`.
   *
   * Does not affect the Save button loading state.
   *
   * @returns Resolves when the archive update finishes.
   */
  const handleArchive = async () => {
    if (isArchiving) {
      return;
    }

    await onArchiveTicket?.();
  };

  return (
    <aside className="support-tickets-details-panel flex h-full flex-col rounded bg-white p-4">
      <div className="grid grid-cols-2 gap-x-4 gap-y-5">
        <SupportTicketDetailField
          label="Patient Name"
          value={details.patientName}
        />
        <SupportTicketDetailField label="MRN" value={details.mrn} />

        <SupportTicketDetailField
          label="Status"
          value={
            canEditStatus ? (
              <Tags
                label={SUPPORT_TICKET_STATUS_LABELS[status]}
                backgroundColor={statusColors.backgroundColor}
                textColor={statusColors.textColor}
                options={SUPPORT_TICKET_EDITABLE_STATUSES.map((value) => ({
                  value,
                  label: SUPPORT_TICKET_STATUS_LABELS[value],
                  backgroundColor:
                    SUPPORT_TICKET_STATUS_TAG_COLORS[value].backgroundColor,
                  textColor: SUPPORT_TICKET_STATUS_TAG_COLORS[value].textColor,
                }))}
                onSelect={(option) =>
                  setStatus(option.value as SupportTicketStatus)
                }
                className="w-fit"
              />
            ) : (
              <span
                className="inline-flex w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold"
                style={{
                  backgroundColor: statusColors.backgroundColor,
                  color: statusColors.textColor,
                }}
              >
                {SUPPORT_TICKET_STATUS_LABELS[status]}
              </span>
            )
          }
        />
        <SupportTicketDetailField label="Priority" value={details.priority} />

        <SupportTicketDetailField label="Created" value={details.created} />
        <SupportTicketDetailField label="Updated" value={details.updated} />

        <div className="col-span-2">
          <SupportTicketDetailField
            label="Opened By"
            value={details.openedBy}
          />
        </div>
      </div>

      {showActionFooter ? (
        <div className="mt-5 flex flex-col gap-2 border-t border-primaryGray-14 pt-4 [&>button]:w-full">
          {canEditStatus ? (
            <Button
              buttonType="primary"
              size="medium"
              onClick={() => {
                void handleSaveStatus();
              }}
              disabled={isSaveDisabled}
            >
              {isUpdatingStatus ? "Saving..." : "Save"}
            </Button>
          ) : null}
          {showArchiveButton ? (
            <Button
              buttonType="secondary"
              disabled={isArchiving}
              size="medium"
              onClick={() => {
                void handleArchive();
              }}
            >
              {isArchiving ? "Archiving..." : "Set as Archive"}
            </Button>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
};

export default SupportTicketDetailsPanel;
